import React, { useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Plus,
  Stethoscope,
  ClipboardCheck,
  Package,
  MapPin,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { useLenis } from "../hooks/useLenis";

const GOLD = "#C9A84C";
const APP_URL = "https://app.smartpeptides.ai";
const SMARTPLAITE_URL = "https://app.smartplaite.com";
const TAPMD_URL = "https://tapmd.ca";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*                                Sub-components                              */
/* -------------------------------------------------------------------------- */

const GoldLabel = ({ children, className = "", center = false }) => (
  <p
    className={`text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase ${center ? "text-center" : ""} ${className}`}
    style={{ color: GOLD }}
  >
    {children}
  </p>
);

const GoldButton = ({ href, external = false, onClick, type = "button", children, "data-testid": testId, className = "" }) => {
  const cls = `inline-flex w-full items-center justify-center gap-2 rounded-full min-h-[56px] px-6 py-3 text-sm font-bold tracking-[0.08em] uppercase text-white shadow-sm active:scale-[0.98] hover:opacity-95 transition-all text-center leading-snug ${className}`;
  const style = { backgroundColor: GOLD };
  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={cls}
        style={style}
        data-testid={testId}
      >
        {children}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls} style={style} data-testid={testId}>
      {children}
    </button>
  );
};

// Card with a prominent gold arc/tab at the top edge
const Card = ({ children, className = "", id, ...rest }) => (
  <div
    id={id}
    className={`relative rounded-2xl bg-white p-6 sm:p-7 scroll-mt-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${className}`}
    style={{ border: `1px solid ${GOLD}44` }}
    {...rest}
  >
    {/* Gold accent arc at top */}
    <span
      aria-hidden="true"
      className="absolute -top-[3px] left-6 right-6 h-[3px] rounded-full"
      style={{ backgroundColor: GOLD }}
    />
    {children}
  </div>
);

const GoldBullet = ({ children }) => (
  <li className="flex items-start gap-3 text-[16px] text-black/85 leading-relaxed">
    <span
      className="mt-2.5 h-2 w-2 rounded-full flex-shrink-0"
      style={{ backgroundColor: GOLD }}
      aria-hidden="true"
    />
    <span>{children}</span>
  </li>
);

const SectionShell = ({ children, className = "", id }) => (
  <section id={id} className={`px-5 sm:px-6 py-12 sm:py-16 ${className}`}>
    <div className="mx-auto max-w-lg">{children}</div>
  </section>
);

const Divider = () => (
  <div
    className="mx-5 sm:mx-6 h-px"
    style={{ backgroundColor: `${GOLD}33` }}
  />
);

/* -------------------------------------------------------------------------- */
/*                            Reorder Account Form                            */
/* -------------------------------------------------------------------------- */

const ReorderForm = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !code) return;
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/reorder`, { email, distributor_code: code });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? "Please check your email and distributor code."
          : detail || "Something went wrong. Please try again."
      );
    }
  };

  const label = "block text-[11px] font-bold tracking-[0.18em] uppercase text-black/60 mb-2";
  const inputCls =
    "w-full h-12 bg-transparent border-0 border-b px-0 text-[15px] text-black placeholder:text-black/35 focus:outline-none focus:border-black transition";

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="reorder-form">
      <div>
        <label className={label}>Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          disabled={status === "loading" || status === "success"}
          data-testid="reorder-email-input"
          className={inputCls}
          style={{ borderBottomColor: `${GOLD}66` }}
        />
      </div>

      <div>
        <label className={label}>Distributor Code</label>
        <input
          type="text"
          required
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. ABRV01"
          disabled={status === "loading" || status === "success"}
          data-testid="reorder-code-input"
          className={`${inputCls} tracking-wider`}
          style={{ borderBottomColor: `${GOLD}66` }}
        />
      </div>

      <GoldButton type="submit" data-testid="reorder-submit">
        {status === "loading" ? "Saving..." : status === "success" ? "Linked — reorder anytime" : "ORDER WITH YOUR DISTRIBUTOR"}
        {status === "success" ? <CheckCircle2 className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
      </GoldButton>

      {status === "success" && (
        <p className="text-sm font-medium text-center" style={{ color: GOLD }} data-testid="reorder-success">
          Your distributor is now linked. No code needed on future orders.
        </p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600 text-center" data-testid="reorder-error">{error}</p>
      )}
    </form>
  );
};

/* -------------------------------------------------------------------------- */
/*                       Coach Directory — Notify Me                          */
/* -------------------------------------------------------------------------- */

const NotifyMeForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/waitlist`, { email, source: "coach-directory-notify" });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="notify-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        disabled={status === "loading" || status === "success"}
        data-testid="notify-email-input"
        className="w-full h-12 rounded-xl bg-white px-4 text-[15px] text-black placeholder:text-black/35 focus:outline-none"
        style={{ border: `1px solid ${GOLD}66` }}
      />
      <GoldButton type="submit" data-testid="notify-submit">
        {status === "loading" ? "Saving..." : status === "success" ? "You're on the list" : "NOTIFY ME WHEN AVAILABLE"}
        {status === "success" ? <CheckCircle2 className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
      </GoldButton>
      {status === "error" && (
        <p className="text-sm text-red-600 text-center">Something went wrong. Please try again.</p>
      )}
    </form>
  );
};

/* -------------------------------------------------------------------------- */
/*                      Coach / Clinician Application                         */
/* -------------------------------------------------------------------------- */

const PROVINCE_OPTIONS = [
  "Remote / Anywhere",
  "Alberta",
  "British Columbia",
  "Saskatchewan",
  "Manitoba",
  "Ontario",
  "Quebec",
  "Nova Scotia",
  "New Brunswick",
  "Newfoundland & Labrador",
  "Prince Edward Island",
  "Yukon",
  "Northwest Territories",
  "Nunavut",
  "United States",
  "International",
];

const CoachApplicationForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [province, setProvince] = useState("Remote / Anywhere");
  const [profession, setProfession] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/coach-applications`, { name, email, province, profession });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Something went wrong.");
    }
  };

  const label = "block text-[11px] font-bold tracking-[0.18em] uppercase text-black/60 mb-2";
  const inputCls =
    "w-full h-12 bg-transparent border-0 border-b px-0 text-[15px] text-black placeholder:text-black/35 focus:outline-none focus:border-black transition";

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="coach-form">
      <GoldLabel>APPLY TO BE LISTED</GoldLabel>

      <div>
        <label className={label}>Name</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name"
          disabled={status === "loading" || status === "success"}
          data-testid="coach-name-input"
          className={inputCls}
          style={{ borderBottomColor: `${GOLD}66` }}
        />
      </div>

      <div>
        <label className={label}>Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          disabled={status === "loading" || status === "success"}
          data-testid="coach-email-input"
          className={inputCls}
          style={{ borderBottomColor: `${GOLD}66` }}
        />
      </div>

      <div>
        <label className={label}>Province (Optional)</label>
        <div className="relative">
          <select
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            disabled={status === "loading" || status === "success"}
            data-testid="coach-province-select"
            className={`${inputCls} appearance-none pr-8 cursor-pointer`}
            style={{ borderBottomColor: `${GOLD}66` }}
          >
            {PROVINCE_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-black/50" />
        </div>
      </div>

      <div>
        <label className={label}>Profession</label>
        <input
          type="text"
          value={profession}
          onChange={(e) => setProfession(e.target.value)}
          placeholder="e.g. Naturopath, MD, Coach, Nurse"
          disabled={status === "loading" || status === "success"}
          data-testid="coach-profession-input"
          className={inputCls}
          style={{ borderBottomColor: `${GOLD}66` }}
        />
      </div>

      <GoldButton type="submit" data-testid="coach-submit">
        {status === "loading" ? "Submitting..." : status === "success" ? "Application received" : "APPLY TO BE LISTED"}
        {status === "success" ? <CheckCircle2 className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
      </GoldButton>

      {status === "success" && (
        <p className="text-sm text-center" style={{ color: GOLD }} data-testid="coach-success">
          Thanks — we'll be in touch as we onboard practitioners.
        </p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}
    </form>
  );
};

/* -------------------------------------------------------------------------- */
/*                                    Page                                    */
/* -------------------------------------------------------------------------- */

const provinces = [
  "Alberta",
  "British Columbia",
  "Saskatchewan",
  "Manitoba",
  "Ontario",
  "Quebec",
];

export default function Landing() {
  useLenis();
  return (
    <div className="min-h-screen bg-white text-black" data-testid="landing-page">
      {/* =========================== SECTION 1 — HERO =========================== */}
      <SectionShell className="pt-10 sm:pt-14 text-center">
        <GoldLabel center data-testid="hero-brand-label">AXIS | SMARTPEPTIDES</GoldLabel>

        <h1
          className="mt-6 text-[44px] sm:text-6xl font-black tracking-[-0.02em] text-black leading-[1.02]"
          data-testid="hero-headline"
        >
          Welcome to Your Peptide Journey.
        </h1>

        <p className="mt-7 text-[19px] sm:text-2xl font-bold text-black leading-snug">
          You probably have a lot of questions right now.
        </p>

        <ul className="mt-8 space-y-4 text-left" data-testid="hero-questions">
          <GoldBullet>What do I do with this vial?</GoldBullet>
          <GoldBullet>How much BAC water do I add?</GoldBullet>
          <GoldBullet>What dose should I take?</GoldBullet>
          <GoldBullet>Could this interact with my medications or supplements?</GoldBullet>
        </ul>

        <p className="mt-8 text-[17px] text-black/75 leading-relaxed">
          Download the SmartPeptides app and get clinical guidance built for exactly
          this moment.
        </p>

        <div className="mt-8">
          <GoldButton href={APP_URL} external data-testid="hero-cta">
            GUIDANCE FOR YOUR PEPTIDE JOURNEY
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
        </div>
      </SectionShell>

      <Divider />

      {/* =========================== SECTION 1.5 — 25% RESULT =========================== */}
      <SectionShell className="text-center">
        <h2
          className="text-[36px] sm:text-5xl font-black tracking-[-0.02em] text-black leading-[1.05]"
          data-testid="result-headline"
        >
          You have the product.{" "}
          <span style={{ color: GOLD }}>That's 25%</span> of your result.
        </h2>
        <p className="mt-6 text-[17px] text-black/75 leading-relaxed max-w-md mx-auto">
          The other 75% is one system — and it's already built for you. Most people
          stop at the vial and wonder why results stall. Let's make sure that's not
          you.
        </p>
      </SectionShell>

      {/* =========================== SECTION 2 — APP CARDS =========================== */}
      <SectionShell>
        <div className="space-y-8">
          <Card data-testid="card-lia">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" style={{ color: GOLD }} />
              <GoldLabel>LIA — PROTOCOL BUILDER</GoldLabel>
            </div>
            <p className="mt-5 text-[16px] text-black/85 leading-relaxed">
              Without a proper protocol — timing, dosing, cycling — you are
              guessing. Guessing means missed windows and wasted vials. Add
              bloodwork, biomarker tracking, and a personalized plan and now you
              have precision.
            </p>
            <div className="mt-6">
              <GoldButton href={APP_URL} external data-testid="lia-cta">
                BUILD MY PROTOCOL WITH LIA
                <ArrowRight className="h-4 w-4" />
              </GoldButton>
            </div>
          </Card>

          <Card data-testid="card-smartplaite">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
              <GoldLabel>SMARTPLAITE — NUTRITION + ACTIVITY</GoldLabel>
            </div>
            <p className="mt-5 text-[16px] text-black/85 leading-relaxed">
              Peptides rebuild. Your nutrition fuels the rebuild and your
              activity drives it. Without both, your body has nothing to work
              with — and your results stall.
            </p>
            <div className="mt-6">
              <GoldButton href={SMARTPLAITE_URL} external data-testid="smartplaite-cta">
                FIX IT WITH SMARTPLAITE
                <ArrowRight className="h-4 w-4" />
              </GoldButton>
            </div>
          </Card>

          <Card data-testid="card-tapmd">
            <div className="flex items-center gap-2">
              <Stethoscope className="h-4 w-4" style={{ color: GOLD }} />
              <GoldLabel>TAPMD — CLINICIAN ACCESS</GoldLabel>
            </div>
            <p className="mt-5 text-[16px] text-black/85 leading-relaxed">
              A clinician confirms your stack is right for your body. Bloodwork,
              medical oversight, and professional guidance close the gap between
              guessing and precision.
            </p>
            <div className="mt-6">
              <GoldButton href={TAPMD_URL} external data-testid="tapmd-cta">
                ACCESS A CLINICIAN VIA TAPMD
                <ArrowRight className="h-4 w-4" />
              </GoldButton>
            </div>
          </Card>
        </div>
      </SectionShell>

      {/* =========================== SECTION 3 — COACH / CLINICIAN (users) =========================== */}
      <SectionShell>
        <GoldLabel center>FIND A COACH OR CLINICIAN ON OUR PLATFORM</GoldLabel>
        <p className="mt-5 text-[17px] text-black/75 leading-relaxed text-center">
          Get personalized support from professionals who understand your protocol.
        </p>

        <div className="mt-8">
          <Card data-testid="card-coach">
            <h3 className="text-2xl font-black tracking-tight text-black">Looking for Guidance?</h3>
            <p className="mt-4 text-[16px] text-black/75 leading-relaxed">
              Connect with certified coaches and clinicians who specialize in
              peptide protocols, precision health, and performance optimization —
              wherever you are.
            </p>
            <div className="mt-6">
              <NotifyMeForm />
            </div>
          </Card>
        </div>
      </SectionShell>

      {/* =========================== SECTION 3.5 — ARE YOU A COACH? =========================== */}
      <SectionShell>
        <Card data-testid="card-coach-apply">
          <h3 className="text-2xl font-black tracking-tight text-black leading-tight">
            Are You a Coach or Clinician?
          </h3>
          <p className="mt-4 text-[16px] text-black/75 leading-relaxed">
            Join the SmartPeptides platform and get discovered by motivated clients
            in your area who are already on protocol.
          </p>
          <div className="mt-7">
            <CoachApplicationForm />
          </div>
        </Card>
      </SectionShell>

      {/* =========================== SECTION 4 — REORDER ACCOUNT =========================== */}
      <SectionShell>
        <Card id="reorder" data-testid="card-reorder">
          <h3 className="text-2xl font-black tracking-tight text-black">Set up your reorder account</h3>
          <p className="mt-4 text-[16px] text-black/75 leading-relaxed">
            Enter your details once. Your distributor stays linked to your email
            forever — no code needed on future orders.
          </p>
          <div className="mt-6">
            <ReorderForm />
          </div>
        </Card>

        <div className="mt-6 space-y-3">
          <GoldButton href={SMARTPLAITE_URL} external data-testid="download-smartplaite-a">
            DOWNLOAD SMARTPLAITE
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
          <GoldButton href={APP_URL} external data-testid="build-protocol-a">
            BUILD MY PROTOCOL WITH LIA
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
        </div>
      </SectionShell>

      {/* =========================== SECTION 5 — TRUSTED SUPPLIER =========================== */}
      <SectionShell>
        <Card data-testid="card-trusted-supplier">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4" style={{ color: GOLD }} />
            <GoldLabel>TRUSTED SUPPLIER</GoldLabel>
          </div>
          <p className="mt-5 text-[17px] text-black/85 leading-relaxed">
            Consistency is the protocol. Running out mid-cycle breaks everything
            you have built. One account. Your distributor stays linked. Always
            stocked, no friction.
          </p>
        </Card>
      </SectionShell>

      {/* =========================== SECTION 6 — FIND YOUR DISTRIBUTOR =========================== */}
      <SectionShell>
        <div className="text-center">
          <div className="inline-flex items-center gap-2">
            <MapPin className="h-4 w-4" style={{ color: GOLD }} />
            <GoldLabel>FIND YOUR DISTRIBUTOR</GoldLabel>
          </div>
          <p className="mt-5 text-[19px] font-medium text-black/85 leading-snug">
            SmartPeptides distributors are available across Canada and internationally.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3" data-testid="province-grid">
          {provinces.map((prov) => (
            <a
              key={prov}
              href={`mailto:hello@smartpeptides.ai?subject=${encodeURIComponent(`Find a distributor — ${prov}`)}`}
              data-testid={`province-${prov.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center justify-center min-h-[76px] px-3 py-4 rounded-2xl text-[15px] font-bold text-black bg-white hover:bg-[#fdf9ec] transition-colors text-center leading-tight"
              style={{ border: `1px solid ${GOLD}77` }}
            >
              {prov}
            </a>
          ))}
        </div>
      </SectionShell>

      {/* =========================== SECTION 7 — ECOSYSTEM =========================== */}
      <SectionShell>
        <div className="space-y-3">
          <GoldButton href={SMARTPLAITE_URL} external data-testid="download-smartplaite-b">
            DOWNLOAD SMARTPLAITE
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
          <GoldButton href={APP_URL} external data-testid="build-protocol-b">
            BUILD MY PROTOCOL WITH LIA
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
          <GoldButton href={TAPMD_URL} external data-testid="tapmd-cta-b">
            ACCESS A CLINICIAN VIA TAPMD
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
          <GoldButton href="#reorder" data-testid="order-distributor-b">
            ORDER WITH YOUR DISTRIBUTOR
            <ArrowRight className="h-4 w-4" />
          </GoldButton>
        </div>

        <div className="mt-14">
          <h3 className="text-[26px] font-black tracking-tight text-black" data-testid="ecosystem-heading">
            Your complete ecosystem:
          </h3>

          <div className="mt-6" data-testid="ecosystem-logos">
            {/* LIA | SmartPeptides — gold compass needle on black */}
            <div className="flex items-center gap-5 py-6" style={{ borderTop: `1px solid ${GOLD}33` }}>
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-black flex-shrink-0 relative">
                <svg viewBox="0 0 64 64" className="h-10 w-10" aria-hidden="true">
                  <circle cx="32" cy="32" r="22" fill="none" stroke={GOLD} strokeWidth="1.5" opacity="0.5" />
                  <polygon points="32,10 38,32 32,26 26,32" fill={GOLD} />
                  <polygon points="32,54 38,32 32,38 26,32" fill={GOLD} opacity="0.55" />
                  <circle cx="32" cy="32" r="2" fill={GOLD} />
                </svg>
              </span>
              <p className="text-[19px] font-bold text-black leading-tight">
                LIA <span className="text-black/40 font-normal">|</span> SmartPeptides
              </p>
            </div>

            {/* Smartplaite — purple gradient monogram on dark */}
            <div className="flex items-center gap-5 py-6" style={{ borderTop: `1px solid ${GOLD}33` }}>
              <span
                className="inline-flex h-16 w-16 items-center justify-center rounded-2xl flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #1a1027 0%, #0d0714 100%)" }}
              >
                <svg viewBox="0 0 64 64" className="h-9 w-9" aria-hidden="true">
                  <defs>
                    <linearGradient id="sp-grad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#c084fc" />
                      <stop offset="60%" stopColor="#8b5cf6" />
                      <stop offset="100%" stopColor="#5b21b6" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 50 32 A 18 18 0 1 1 32 14"
                    fill="none"
                    stroke="url(#sp-grad)"
                    strokeWidth="6"
                    strokeLinecap="round"
                  />
                  <circle cx="50" cy="17" r="3.4" fill="url(#sp-grad)" />
                </svg>
              </span>
              <p className="text-[19px] font-bold text-black leading-tight">Smartplaite</p>
            </div>

            {/* TapMD */}
            <div
              className="flex items-center justify-center gap-3 py-6"
              style={{ borderTop: `1px solid ${GOLD}33`, borderBottom: `1px solid ${GOLD}33` }}
            >
              <Plus className="h-7 w-7" strokeWidth={3} style={{ color: "#5AB8B0" }} />
              <span className="text-[26px] font-medium tracking-tight" style={{ color: "#5AB8B0" }}>tapMD</span>
            </div>
          </div>
        </div>
      </SectionShell>

      {/* =========================== FOOTER =========================== */}
      <footer
        className="mt-4 px-6 sm:px-8 pt-10 pb-8 text-white"
        style={{ backgroundColor: "#0b1f4e" }}
        data-testid="site-footer"
      >
        <div className="mx-auto max-w-lg">
          <p className="text-[26px] sm:text-3xl font-black tracking-tight leading-tight">
            Precision Peptides. Engineered for Results.
          </p>

          <div className="mt-8 space-y-3 text-[13px] tracking-[0.18em] uppercase font-medium">
            <a href="/privacy" className="block text-white/70 hover:text-white" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms" className="block text-white/70 hover:text-white" data-testid="footer-terms">Terms of Use</a>
            <p className="text-white/70">Powered by SmartPeptides.ai</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
